import { useCallback, useState } from "react";
import { useSession } from "next-auth/react";

/**
 * Single source of truth for leaving an impersonation session, shared by the
 * top navigation and the dashboard error state.
 *
 * Triggers the next-auth session update that restores the original (admin)
 * account, then forces a full reload so every cached request and UI state is
 * refetched with the restored session. Only `leave_impersonation` is sent — the
 * backend restores from the JWT's `admin_user`, so we don't echo back a
 * possibly-stale client session.
 */
export default function useLeaveImpersonation() {
  const { data: session, update } = useSession();
  const [leaving, setLeaving] = useState(false);

  const leaveImpersonation = useCallback(async () => {
    setLeaving(true);
    try {
      await update({ leave_impersonation: true });
    } catch (e) {
      // The update can throw if the client session is malformed (e.g. after a
      // deploy). The reload below still recovers whatever session the server
      // settled on.
    }
    window.location.reload();
  }, [update]);

  return {
    isImpersonating: Boolean(session?.admin_user),
    leaving,
    leaveImpersonation,
  };
}
